import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import { EnhancedBreadcrumb } from '../../components/ui/breadcrumb.jsx';
import { usePageMeta } from '../../components/BreadcrumbProvider.jsx';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useBooking } from '../../contexts/BookingContext';
import { TRADEMARK_DEFINITIONS } from '../../constants/contentConfig';

const SkillStackStaffingSolutions = () => {
  const { openBooking } = useBooking();
  
  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'SkillStack Staffing', href: '/skillstack-staffing-solutions' }
  ];

  // Set page metadata with breadcrumbs
  usePageMeta({
    title: 'SkillStack Staffing Solutions',
    breadcrumbs: breadcrumbs
  });

  // Handle anchor scrolling
  useAnchorScroll();

  useEffect(() => {
    // Smooth scroll behavior for anchor links
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Cleanup on unmount
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const staffingModels = [
    {
      name: 'Contract Staffing',
      icon: 'Clock',
      description: 'Flexible workforce for short- or mid-term needs',
      features: [
        '25-day delivery guarantee',
        'SkillStack™ verification included',
        'Payroll and compliance handled',
        'Scale up/down as needed',
        'Project-based or time-based contracts'
      ],
      timeline: '<25 days',
      idealFor: 'Seasonal demands, specialized projects, capacity scaling',
      pricing: 'Transparent hourly rates'
    },
    {
      name: 'Contract-to-Hire',
      icon: 'RefreshCw',
      description: 'Trial-to-hire model for mutual fit assessment',
      features: [
        '25-day trial period included',
        'Convert to FTE anytime',
        'Risk-free evaluation',
        'Cultural fit assessment',
        'Performance-based conversion'
      ],
      timeline: '<25 days + trial',
      idealFor: 'Critical roles, skill validation, team integration',
      pricing: 'Contract rate + conversion fee'
    },
    {
      name: 'Full-Time (FTE)',
      icon: 'Users',
      description: 'Permanent talent for long-term strategic roles',
      features: [
        '25-day placement guarantee',
        '90-day replacement warranty',
        'Complete recruitment lifecycle',
        'Executive onboarding support',
        'Long-term success planning'
      ],
      timeline: '<25 days',
      idealFor: 'Core team positions, leadership roles, critical functions',
      pricing: 'One-time placement fee'
    }
  ];

  const candidateExperience = [
    {
      title: 'Pre-Boarding Engagement',
      description: 'We start the experience before day one with personalized welcome kits and team introductions.',
      features: [
        'Digital welcome kits with company branding',
        'Pre-join team introductions and buddy assignment',
        'Equipment setup and IT access coordination',
        'Personalized onboarding schedules',
        'Cultural orientation materials'
      ],
      icon: 'Gift'
    },
    {
      title: 'Luxury Transportation',
      description: 'Premium pickup and drop services ensuring candidates arrive stress-free and focused.',
      features: [
        'Airport transfers for out-of-town candidates',
        'Daily pickup/drop for interview cycles',
        'Luxury vehicle options for senior positions',
        'Real-time tracking and communication',
        'Flexible scheduling around candidate availability'
      ],
      icon: 'Car'
    },
    {
      title: 'Experience Analytics',
      description: 'Real-time feedback loops and sentiment analysis to continuously improve the journey.',
      features: [
        'Candidate satisfaction surveys after each touchpoint',
        'Hiring manager experience scoring',
        'Time-to-hire and drop-off analytics',
        'Experience heatmaps identifying friction points',
        'Continuous improvement recommendations'
      ],
      icon: 'BarChart3'
    }
  ];

  const retentionGuarantees = [
    {
      title: '15-Day Replacement Guarantee',
      description: 'Free replacements within 15 days for contract roles and during trial period for C2H.',
      coverage: 'Contract & C2H positions',
      process: 'Immediate replacement sourcing with priority processing',
      icon: 'ShieldCheck'
    },
    {
      title: '90-Day Performance Warranty',
      description: 'Full replacement guarantee for FTE hires with performance analytics.',
      coverage: 'Full-time positions',
      process: 'Performance monitoring and support throughout warranty period',
      icon: 'Award'
    },
    {
      title: 'Conversion Analytics',
      description: 'Predictive analytics to identify early retention risks and intervention opportunities.',
      coverage: 'All hiring models',
      process: 'Monthly retention reports and proactive support',
      icon: 'TrendingUp'
    }
  ];

  const expertiseAreas = [
    {
      category: 'Technology & Engineering',
      roles: ['Full Stack Developers', 'DevOps Engineers', 'Data Scientists', 'Cloud Architects', 'Cybersecurity Specialists'],
      demand: 'High',
      timeline: '20-25 days'
    },
    {
      category: 'Operations & Support',
      roles: ['Operations Managers', 'Process Analysts', 'Quality Assurance', 'Customer Success', 'Technical Support'],
      demand: 'Very High',
      timeline: '15-20 days'
    },
    {
      category: 'Finance & Accounting',
      roles: ['Financial Analysts', 'Accountants', 'Tax Specialists', 'Audit Professionals', 'FP&A Experts'],
      demand: 'High',
      timeline: '20-25 days'
    },
    {
      category: 'HR & Talent Acquisition',
      roles: ['HR Business Partners', 'Talent Acquisition Specialists', 'L&D Professionals', 'HR Operations', 'Compensation Analysts'],
      demand: 'Medium',
      timeline: '18-22 days'
    }
  ];

  return (
    <>
      <Helmet>
        <title>SkillStack Staffing Solutions | Bootminds Elite</title>
        <meta 
          name="description" 
          content="Contract, contract-to-hire, and full-time staffing solutions with 25-day delivery guarantee. Powered by SkillStack™ intelligence with candidate experience engine and retention guarantees." 
        />
        <meta 
          name="keywords" 
          content="staffing solutions, contract staffing, contract to hire, full-time recruitment, SkillStack, talent acquisition, 25-day guarantee" 
        />
        <link rel="canonical" href="/services/staffing" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Breadcrumb Navigation */}
        <div className="bg-background border-b border-border">
          <div className="container-executive py-4">
            <EnhancedBreadcrumb items={breadcrumbs} />
          </div>
        </div>
        
        <main>
          {/* Hero Section */}
          <section className="py-20 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary mb-6">
                  <Icon name="Layers" size={16} />
                  <span>SkillStack™ Staffing Solutions</span>
                </div>
                <h1 className="text-4xl lg:text-6xl font-headline font-bold text-text-primary mb-6">
                  Staffing that Launches Teams in
                  <span className="text-accent"> Under 25 Days</span>
                </h1>
                <p className="text-xl text-text-secondary mb-8 leading-relaxed">
                  Contract, contract-to-hire, and full-time hiring models powered by SkillStack™ verification,
                  AI-backed analytics, and a candidate-first experience. Choose the engagement that meets your velocity and conversion goals.
                </p>
                <div className="mx-auto mb-8 max-w-3xl rounded-xl border border-border bg-card/70 p-5 text-left">
                  <p className="text-sm text-text-secondary">
                    <span className="font-semibold text-text-primary">{TRADEMARK_DEFINITIONS.SkillStack.name}: </span>
                    {TRADEMARK_DEFINITIONS.SkillStack.long}
                  </p>
                </div>
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    size="lg"
                    iconName="Users"
                    onClick={() => openBooking('staffing_hero')}
                  >
                    Start Staffing Request
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Staffing Models */}
          <section id="staffing-models" className="py-20 bg-background">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Flexible Staffing Models
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Choose the engagement model that best fits your timeline, budget, and strategic objectives
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-3">
                {staffingModels.map((model, index) => (
                  <div key={index} className="group">
                    <div className="h-full bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                        <Icon name={model.icon} size={32} className="text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold text-text-primary mb-4">{model.name}</h3>
                      <p className="text-text-secondary mb-6">{model.description}</p>
                      <ul className="space-y-3 mb-6">
                        {model.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3 text-sm text-text-secondary">
                            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="border-t border-border pt-6 space-y-3">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-text-secondary">Timeline:</span>
                          <span className="font-medium text-text-primary">{model.timeline}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-text-secondary">Ideal For:</span>
                          <span className="font-medium text-text-primary text-right">{model.idealFor}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-text-secondary">Pricing:</span>
                          <span className="font-medium text-accent">{model.pricing}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Expertise Areas */}
          <section id="expertise-areas" className="py-20 bg-background-alt">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Expertise Across Key Functions
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Deep talent pools across technology, operations, finance, and HR functions
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-2">
                {expertiseAreas.map((area, index) => (
                  <div key={index} className="bg-card border border-border rounded-2xl p-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-text-primary">{area.category}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        area.demand === 'Very High' ? 'bg-red-100 text-red-800' :
                        area.demand === 'High' ? 'bg-orange-100 text-orange-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {area.demand} Demand
                      </span>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-medium text-text-primary mb-3">Key Roles:</h4>
                      <div className="flex flex-wrap gap-2">
                        {area.roles.map((role, idx) => (
                          <span key={idx} className="px-3 py-1 bg-muted rounded-full text-sm text-text-secondary">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-text-secondary">Typical Timeline:</span>
                      <span className="font-medium text-text-primary">{area.timeline}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Candidate Experience Engine */}
          <section id="candidate-experience" className="py-20 bg-background">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Candidate Experience Engine
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Every candidate is treated like a brand ambassador—because their experience dictates your market reputation
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-3">
                {candidateExperience.map((experience, index) => (
                  <div key={index} className="bg-card border border-border rounded-2xl p-8 shadow-lg">
                    <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mb-6">
                      <Icon name={experience.icon} size={32} className="text-accent" />
                    </div>
                    <h3 className="text-xl font-semibold text-text-primary mb-4">{experience.title}</h3>
                    <p className="text-text-secondary mb-6">{experience.description}</p>
                    <ul className="space-y-2">
                      {experience.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3 text-sm text-text-secondary">
                          <Icon name="ArrowRight" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Retention & Guarantees */}
          <section id="retention" className="py-20 bg-background-alt">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Retention Guarantees & Risk Mitigation
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  From replacements to compliance, every staffing engagement is governed by measurable obligations
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-3">
                {retentionGuarantees.map((guarantee, index) => (
                  <div key={index} className="bg-card border border-border rounded-2xl p-8 shadow-lg">
                    <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mb-6">
                      <Icon name={guarantee.icon} size={32} className="text-success" />
                    </div>
                    <h3 className="text-xl font-semibold text-text-primary mb-4">{guarantee.title}</h3>
                    <p className="text-text-secondary mb-6">{guarantee.description}</p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-text-secondary">Coverage:</span>
                        <span className="font-medium text-text-primary">{guarantee.coverage}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-text-secondary">Process:</span>
                        <span className="font-medium text-text-primary text-right">{guarantee.process}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* TA Strategy Report */}
          <section id="ta-strategy" className="py-20 bg-background">
            <div className="container-executive">
              <div className="grid gap-12 lg:grid-cols-2 items-center">
                <div>
                  <div className="inline-flex items-center space-x-2 rounded-full bg-accent/10 px-4 py-2 text-sm font-medium text-accent mb-6">
                    <Icon name="ClipboardList" size={16} />
                    <span>TA Strategy Report</span>
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-6">
                    Workshop-Driven TA Blueprint
                  </h2>
                  <p className="text-lg text-text-secondary mb-8 leading-relaxed">
                    A comprehensive talent acquisition strategy report built through collaborative workshops with your HR, business, and CXO teams. Get a customized hiring narrative with an implementation roadmap.
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    {[
                      { icon: 'Target', text: 'Stakeholder interviews to understand your unique talent challenges' },
                      { icon: 'Brain', text: 'AI impact assessment on your workforce and skills requirements' },
                      { icon: 'FileText', text: 'Comprehensive TA blueprint with hiring priorities and timelines' },
                      { icon: 'TrendingUp', text: 'Market benchmarking and competitive positioning analysis' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name={item.icon} size={20} className="text-accent" />
                        </div>
                        <p className="text-text-secondary pt-2">{item.text}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-secondary">Fixed Investment</p>
                        <p className="text-2xl font-bold text-text-primary">$10,000</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-text-secondary">Delivery</p>
                        <p className="text-lg font-semibold text-accent">2-3 Weeks</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-3xl p-8 border border-border">
                  <h3 className="text-xl font-semibold text-text-primary mb-6">What You Get</h3>
                  <ul className="space-y-4">
                    {[
                      'Executive summary for leadership buy-in',
                      'Role-by-role hiring plan with skill matrices',
                      'Compensation benchmarking data',
                      'Employer brand positioning recommendations',
                      'Technology stack recommendations for TA',
                      '90-day implementation roadmap',
                      'Success metrics and KPI framework'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-3 text-text-secondary">
                        <Icon name="CheckCircle" size={18} className="text-success flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Workshop Methodology */}
          <section id="workshop" className="py-20 bg-background-alt">
            <div className="container-executive">
              <div className="text-center mb-16">
                <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary mb-6">
                  <Icon name="Users2" size={16} />
                  <span>Workshop Methodology</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Collaborative Discovery Process
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Our workshop methodology brings together HR, business, and CXO teams to co-create a talent strategy aligned with your business objectives
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-3 mb-12">
                {[
                  {
                    phase: 'Phase 1',
                    title: 'Discovery & Alignment',
                    duration: '2-3 days',
                    icon: 'Search',
                    activities: [
                      'Stakeholder interviews (HR, Hiring Managers, CXOs)',
                      'Current state assessment',
                      'Pain point identification',
                      'Success criteria definition'
                    ]
                  },
                  {
                    phase: 'Phase 2',
                    title: 'Analysis & Design',
                    duration: '3-5 days',
                    icon: 'BarChart3',
                    activities: [
                      'Market and compensation benchmarking',
                      'AI impact on roles assessment',
                      'Skills gap analysis',
                      'Hiring priority matrix creation'
                    ]
                  },
                  {
                    phase: 'Phase 3',
                    title: 'Blueprint & Roadmap',
                    duration: '2-3 days',
                    icon: 'Map',
                    activities: [
                      'TA strategy document creation',
                      'Implementation roadmap',
                      'Technology recommendations',
                      'Executive presentation'
                    ]
                  }
                ].map((phase, index) => (
                  <div key={index} className="bg-card border border-border rounded-2xl p-8 shadow-lg">
                    <div className="flex items-center justify-between mb-6">
                      <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                        {phase.phase}
                      </span>
                      <span className="text-sm text-text-secondary">{phase.duration}</span>
                    </div>
                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                      <Icon name={phase.icon} size={28} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-text-primary mb-4">{phase.title}</h3>
                    <ul className="space-y-3">
                      {phase.activities.map((activity, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                          <Icon name="ChevronRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              
              <div className="bg-card border border-border rounded-2xl p-8 flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                    <Icon name="Sparkles" size={32} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">Cross-Functional Collaboration</h3>
                    <p className="text-text-secondary">HR, business, and CXO teams work together to create a unified talent strategy</p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  iconName="Calendar"
                  onClick={() => openBooking('workshop_cta')}
                >
                  Schedule Workshop Discovery
                </Button>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary to-accent text-primary-foreground">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold mb-6">
                  Ready to Build Your Team in Under 25 Days?
                </h2>
                <p className="text-xl mb-8 opacity-90">
                  Schedule a consultation with our staffing experts to discuss your talent requirements and get a customized hiring plan.
                </p>
                <div className="flex justify-center">
                  <Button
                    variant="secondary"
                    size="lg"
                    iconName="Calendar"
                    onClick={() => openBooking('staffing_cta')}
                  >
                    Schedule Staffing Consultation
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <SharedFooter />
      </div>
    </>
  );
};

export default SkillStackStaffingSolutions;
